export interface ExtractedData {
  vendor_name?: string;
  total_amount?: number;
  currency?: string;
  transaction_date?: string;
  line_items?: Array<{
    description: string;
    amount: number;
    quantity?: number;
  }>;
  tax_amount?: number;
  payment_method?: string;
  raw_text?: string;
}

export interface DocumentUploadResponse {
  document_id: string;
  extracted_data: ExtractedData;
  confidence_score: number;
  processing_status: string;
  needs_review: boolean;
  presigned_url?: string;
  thumbnail_url?: string;
}

export interface DocumentResponse {
  id: string;
  filename: string;
  file_size: number;
  content_type: string;
  s3_key?: string;
  thumbnail_key?: string;
  extracted_data?: ExtractedData;
  verified_data?: ExtractedData;
  confidence_score?: number;
  processing_status: string;
  is_verified: boolean;
  uploaded_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentListResponse {
  documents: DocumentResponse[];
  total: number;
  page: number;
  size: number;
  has_next: boolean;
}

export interface PresignedUrlResponse {
  url: string;
  expires_in: number;
  expires_at: string;
}

// Simple document API functions that use the existing useApiClient hook pattern

/**
 * Document API functions - use these with the useApiClient hook
 * Example: const apiClient = useApiClient(); await uploadDocument(apiClient, file);
 */

/**
 * Upload and process a document
 */
export const uploadDocument = async (
  apiClient: any,
  file: File,
  expenseId?: string,
  description?: string,
  options?: { skipProcessing?: boolean; timeoutMs?: number; onUploadProgress?: (evt: any) => void }
): Promise<DocumentUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  if (expenseId) {
    formData.append('expense_id', expenseId);
  }

  if (description) {
    formData.append('description', description);
  }

  if (options?.skipProcessing) {
    formData.append('skip_processing', 'true');
  }

  // Use multipart helper with extended timeout (defaults to 5 minutes in client)
  return apiClient.postMultipart('/documents/upload', formData, {
    timeoutMs: options?.timeoutMs,
    onUploadProgress: options?.onUploadProgress,
  });
};

/**
 * Get extracted data for a document
 */
export const getExtractedData = async (
  apiClient: any,
  documentId: string
): Promise<DocumentResponse> => {
  return apiClient.get(`/documents/${documentId}/extracted-data`);
};

/**
 * Verify extracted data
 */
export const verifyExtractedData = async (
  apiClient: any,
  documentId: string, 
  verifiedData: Partial<ExtractedData>
): Promise<{ document_id: string; verified_data: ExtractedData; is_verified: boolean; updated_at: string }> => {
  return apiClient.post(`/documents/${documentId}/verify`, verifiedData);
};

/**
 * Get presigned URL for document download
 */
export const getDownloadUrl = async (
  apiClient: any,
  documentId: string, 
  expiration: number = 3600
): Promise<PresignedUrlResponse> => {
  return apiClient.get(`/documents/${documentId}/download-url?expiration=${expiration}`);
};

/**
 * List user's documents
 */
export const listDocuments = async (
  apiClient: any,
  page: number = 1,
  size: number = 20,
  processingStatus?: string
): Promise<DocumentListResponse> => {
  let url = `/documents/?page=${page}&size=${size}`;
  
  if (processingStatus) {
    url += `&processing_status=${processingStatus}`;
  }

  return apiClient.get(url);
};

/**
 * Delete a document
 */
export const deleteDocument = async (
  apiClient: any,
  documentId: string
): Promise<void> => {
  return apiClient.delete(`/documents/${documentId}`);
};
